import { AlertTriangle, FlaskConical } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { EmptyState, StatusPill } from '@/components/app';
import { ExperimentAnalytics } from '../../../api/pricingVariants';
import { formatMoney } from '../../Plans/planFormat';

interface FunnelTableProps {
  analytics: ExperimentAnalytics | null;
  isLoading: boolean;
}

const num = 'whitespace-nowrap text-right tabular-nums';

/**
 * Воронка по вариантам: назначено → заказ → оплата → выручка → ARPU.
 *
 * ARPU, а не конверсия, — решающая метрика (ADR 0014): при кратном разбросе
 * дешёвый вариант почти гарантированно выигрывает по конверсии и может при
 * этом проиграть по деньгам.
 */
export function FunnelTable({ analytics, isLoading }: FunnelTableProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col" aria-busy="true" aria-label="Загрузка воронки">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex items-center gap-6 border-t border-border-soft px-[18px] py-3.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="ml-auto h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (!analytics || analytics.variants.length === 0) {
    return (
      <EmptyState
        className="border-t border-border-soft"
        icon={<FlaskConical />}
        title="Вариантов пока нет"
        description="Создайте контрольный вариант и один-два тестовых — замер начнётся с момента запуска набора."
      />
    );
  }

  const best = analytics.variants
    .filter((v) => v.is_readable)
    .sort((a, b) => b.arpu - a.arpu)[0];

  return (
    <Table className="min-w-[960px]">
      <TableHeader>
        <TableRow>
          <TableHead>Вариант</TableHead>
          <TableHead className="text-right">Доля набора</TableHead>
          <TableHead className="text-right">Назначено</TableHead>
          <TableHead className="text-right">Создали заказ</TableHead>
          <TableHead className="text-right">Оплатили</TableHead>
          <TableHead className="text-right">Заказ → оплата</TableHead>
          <TableHead className="text-right">Конверсия</TableHead>
          <TableHead className="text-right">Выручка</TableHead>
          <TableHead className="text-right">ARPU</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {analytics.variants.map((row) => {
          const isBest = best?.id === row.id;
          return (
            <TableRow key={row.id} className={cn(isBest && 'bg-pos-soft hover:bg-pos-soft')}>
              <TableCell>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{row.name}</span>
                  {row.is_control && (
                    <StatusPill size="sm" tone="neutral">
                      контроль
                    </StatusPill>
                  )}
                  {row.status === 'archived' && (
                    <StatusPill size="sm" tone="neg">
                      архив
                    </StatusPill>
                  )}
                  {isBest && (
                    <StatusPill size="sm" tone="pos">
                      лучший ARPU
                    </StatusPill>
                  )}
                </div>
                {row.transferred > 0 && (
                  <p className="m-0 mt-1 text-xs text-text-2">
                    переведено и исключено из сравнения: <span className="tabular-nums">{row.transferred}</span>
                  </p>
                )}
              </TableCell>
              <TableCell className={num}>{row.status === 'active' ? `${row.share_percent}%` : '—'}</TableCell>
              <TableCell className={cn(num, 'font-semibold')}>{row.assigned}</TableCell>
              <TableCell className={num}>
                {row.users_with_orders}
                <span className="ml-1 text-xs text-text-2">/ {row.orders_count} шт</span>
              </TableCell>
              <TableCell className={num}>
                <span className="inline-flex items-center gap-1.5">
                  {!row.is_readable && row.assigned > 0 && (
                    <span
                      className="text-warn"
                      title={`Меньше ${analytics.min_payments_for_decision} оплат — по этой группе решение не принимается`}
                    >
                      <AlertTriangle className="size-3.5" aria-hidden />
                      <span className="sr-only">
                        Мало оплат — меньше {analytics.min_payments_for_decision}, решение по группе не принимается
                      </span>
                    </span>
                  )}
                  <span className="font-semibold">{row.paid_orders}</span>
                </span>
              </TableCell>
              <TableCell className={num}>{row.order_to_payment_rate}%</TableCell>
              <TableCell className={num}>{row.conversion}%</TableCell>
              <TableCell className={num}>{formatMoney(row.revenue)}</TableCell>
              <TableCell className={cn(num, 'font-display font-bold', isBest ? 'text-pos' : 'text-text')}>
                {formatMoney(row.arpu)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
